/**
 * app.js
 */

import React from  'react';
import { BrowserRouter } from "react-router-dom";
import { StyledEngineProvider } from '@mui/material/styles';
import { ThemeProvider } from 'contexts/theme/theme-context';
import { LocalizationProvider } from '@mui/x-date-pickers';
import NotifyProvider from 'components/notify';
import NetworkConnectionProvider from 'contexts/network-connection/network-connection.provider';
import { UserProvider } from 'contexts/user/user-context';
import { DogsProvider } from 'contexts/dogs/dogs-context';
import { AuthProvider } from 'contexts/auth/auth-context';
import { TourProvider } from 'contexts/tour/tour-context';
import { AppMessagingProvider } from 'contexts/app-messaging/app-messaging-context';
import { AppUtilityProvider } from 'contexts/app-utility/app-utility-context';
import NotificationsProvider from 'contexts/notifications/notifications.provider';
import { AppBarProvider } from 'contexts/app-bar/app-bar-context';
import { TrackingProviders } from 'components/tracking/tracking-providers';
import { CssBaseline } from '@mui/material';
import AxiosConfig from 'utils/axios-config';
import PublicRouter from 'routing/public-router';
import UserState from 'utils/user-state';
import Layout from 'components/layout';
import * as Sentry from "@sentry/react";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import logger from 'utils/logger';
import './app.css';

// global utility imports
import 'utils/string';


// initialize the Sentry online logging service as early as possible
if (process.env.REACT_APP_ENV !== 'development') {
  Sentry.init({
    dsn: "https://48c5e48d9ffc4157bc6ce5ffe3aa07e1@o877043.ingest.sentry.io/5827267",
    release: "packtrack@2.0.0",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Allow Sentry to record all details during error playback
        maskAllText: false,
        blockAllMedia: false,        
      }),
    ],
    tracePropagationTargets: ["https://mypacktrack.com", "https://api.mypacktrack.com"],
    environment: "production",
    tracesSampleRate: 0.05,
    
    // Capture Replay for 0.5% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.005,
    replaysOnErrorSampleRate: 1.0,
  });
}


// Initialize the Intercom help system
(function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/o89sg5k0';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();


function App() {
  logger.logNotice('Starting PACKTRACK app');


  return (
    <div className="App">
      <BrowserRouter>
        <StyledEngineProvider injectFirst>
          <ThemeProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <NotifyProvider>
                <NetworkConnectionProvider>
                  <AppUtilityProvider>
                    <UserProvider>
                      <DogsProvider>
                        <AppMessagingProvider>
                          <AuthProvider>
                            <TourProvider>
                              <NotificationsProvider>
                                <AppBarProvider>
                                  <TrackingProviders ids={['track', 'map', 'live', 'drawer', 'form']}>
                                    <CssBaseline />
                                    <AxiosConfig />
                                    <PublicRouter>
                                      <UserState />
                                      <Layout />
                                    </PublicRouter>
                                  </TrackingProviders>
                                </AppBarProvider>
                              </NotificationsProvider>
                            </TourProvider>
                          </AuthProvider>
                        </AppMessagingProvider>
                      </DogsProvider>
                    </UserProvider>
                  </AppUtilityProvider>
                </NetworkConnectionProvider>
              </NotifyProvider>
            </LocalizationProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
